import React from 'react'

import {
  type OnboardingEnrollment,
  type OnboardingInfo,
} from 'components/member_onboarding_v2/types'
import {
  isEnrollmentEventRenewal,
  isSameMedicalPlanAcrossYears,
} from 'components/member_onboarding_v2/utils'
import { listToProseString } from 'components/members/utils'
import { capitalize } from 'utils'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingRenewalDentalPlan,
  type MemberOnboardingRenewalMedicalPlan,
  type MemberOnboardingRenewalVisionPlan,
} from 'utils/web_api.schemas'

import { PartialCoverageWarningBanner } from '../PartialCoverageWarningBanner/PartialCoverageWarningBanner'
import RenewalsConfirmComparisonTable from '../RenewalsConfirmComparisonTable/RenewalsConfirmComparisonTable'

interface SelectedPlansComparisonTableProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  onboardingData: OnboardingInfo;
}

type PlanWithType = {
  name: string;
  type: 'medical' | 'dental' | 'vision';
}

export const getCurrentYearPlanNames = ({
  currentMedicalPlan,
  currentDentalPlan,
  currentVisionPlan,
}: {
  currentMedicalPlan?: MemberOnboardingRenewalMedicalPlan;
  currentDentalPlan?: MemberOnboardingRenewalDentalPlan;
  currentVisionPlan?: MemberOnboardingRenewalVisionPlan;
}): PlanWithType[] => {
  const plans: PlanWithType[] = []

  if (currentMedicalPlan) {
    plans.push({
      name:
        currentMedicalPlan.carrier_name + ' ' + currentMedicalPlan.plan_name,
      type: 'medical',
    })
  }

  if (currentDentalPlan) {
    plans.push({
      name:
        currentDentalPlan.carrier_name +
        ' ' +
        capitalize(currentDentalPlan.plan_code),
      type: 'dental',
    })
  }

  if (currentVisionPlan) {
    plans.push({
      name:
        currentVisionPlan.carrier_name +
        ' ' +
        capitalize(currentVisionPlan.plan_code),
      type: 'vision',
    })
  }

  return plans
}

export const getNextYearPlanNames = ({
  nextMedicalPlan,
  nextDentalPlan,
  nextVisionPlan,
}: {
  nextMedicalPlan?: OnboardingEnrollment;
  nextDentalPlan?: OnboardingEnrollment;
  nextVisionPlan?: OnboardingEnrollment;
}): PlanWithType[] => {
  const plans: PlanWithType[] = []

  if (nextMedicalPlan) {
    plans.push({
      name: nextMedicalPlan.carrier_name + ' ' + nextMedicalPlan.display_name,
      type: 'medical',
    })
  }

  if (nextDentalPlan) {
    plans.push({
      name:
        nextDentalPlan.carrier_name +
        ' ' +
        capitalize(nextDentalPlan.display_name ?? ''),
      type: 'dental',
    })
  }

  if (nextVisionPlan) {
    plans.push({
      name:
        nextVisionPlan.carrier_name +
        ' ' +
        capitalize(nextVisionPlan.display_name ?? ''),
      type: 'vision',
    })
  }

  return plans
}

export const categorizePlanChanges = ({
  currentMedicalPlan,
  currentDentalPlan,
  currentVisionPlan,
  nextMedicalPlan,
  nextDentalPlan,
  nextVisionPlan,
}: {
  currentMedicalPlan?: MemberOnboardingRenewalMedicalPlan;
  currentDentalPlan?: MemberOnboardingRenewalDentalPlan;
  currentVisionPlan?: MemberOnboardingRenewalVisionPlan;
  nextMedicalPlan?: OnboardingEnrollment;
  nextDentalPlan?: OnboardingEnrollment;
  nextVisionPlan?: OnboardingEnrollment;
}): {
  added: string[];
  removed: string[];
  changed: string[];
  same: string[];
} => {
  const added: string[] = []
  const removed: string[] = []
  const changed: string[] = []
  const same: string[] = []

  // Medical plan analysis
  if (!currentMedicalPlan && nextMedicalPlan) {
    added.push('medical')
  } else if (currentMedicalPlan && !nextMedicalPlan) {
    removed.push('medical')
  } else if (currentMedicalPlan && nextMedicalPlan) {
    if (
      isSameMedicalPlanAcrossYears(
        currentMedicalPlan.plan_code,
        nextMedicalPlan.plan_code
      )
    ) {
      same.push('medical')
    } else {
      changed.push('medical')
    }
  }

  // Dental plan analysis
  if (!currentDentalPlan && nextDentalPlan) {
    added.push('dental')
  } else if (currentDentalPlan && !nextDentalPlan) {
    removed.push('dental')
  } else if (currentDentalPlan && nextDentalPlan) {
    if (currentDentalPlan.plan_code !== nextDentalPlan.plan_code) {
      changed.push('dental')
    } else {
      same.push('dental')
    }
  }

  // Vision plan analysis
  if (!currentVisionPlan && nextVisionPlan) {
    added.push('vision')
  } else if (currentVisionPlan && !nextVisionPlan) {
    removed.push('vision')
  } else if (currentVisionPlan && nextVisionPlan) {
    if (currentVisionPlan.plan_code !== nextVisionPlan.plan_code) {
      changed.push('vision')
    } else {
      same.push('vision')
    }
  }

  return { added, removed, changed, same }
}

export const generatePlanSubtitle = (
  categories: {
    added: string[];
    removed: string[];
    changed: string[];
    same: string[];
  },
  coveredPlans: string[],
  isRenewal: boolean
): string => {
  const { added, removed, changed, same } = categories
  const formatPlanTypes = (planTypes: string[]): string => {
    return listToProseString(planTypes)
  }

  const nextYearText = isRenewal ? ' next year' : ''

  // Case 1: No plan changes
  if (added.length === 0 && removed.length === 0 && changed.length === 0) {
    return `You'll have the same plans${nextYearText}`
  }

  // Case 2: Opted out of everything when previously enrolled
  if (
    removed.length > 0 &&
    added.length === 0 &&
    changed.length === 0 &&
    same.length === 0
  ) {
    return `You won't have any coverage (${listToProseString(
      coveredPlans,
      'or'
    )}) through us${nextYearText}`
  }

  // Case 5: Some plans changed and some plans opted out (check this before "all new plans")
  if (changed.length > 0 && removed.length > 0) {
    const changedText = `Your ${formatPlanTypes(changed)} ${
      changed.length === 1 ? 'plan will' : 'plans will'
    } change${nextYearText}`
    const removedText = `you'll no longer be enrolled in ${formatPlanTypes(
      removed
    )}`
    return `${changedText}, and ${removedText}`
  }

  // Case 3: All plans changed (D+V offered on thatch)
  // This happens when all plans are either added or changed, and none are the same
  // AND we have multiple plan types (medical + dental/vision)
  const totalPlans =
    added.length + removed.length + changed.length + same.length
  if (
    same.length === 0 &&
    (added.length > 0 || changed.length > 0) &&
    totalPlans > 1
  ) {
    return `You'll have all new plans${nextYearText}`
  }

  // Case 4: Changed medical (D+V not offered on thatch)
  // This happens when only medical is changed and no dental/vision plans exist
  if (
    changed.length === 1 &&
    changed[0] === 'medical' &&
    added.length === 0 &&
    removed.length === 0 &&
    same.length === 0
  ) {
    return `You'll have a new plan${nextYearText}`
  }

  // Case 6: Some, but not all plans changed
  if (changed.length > 0 && same.length > 0) {
    const changedText = `Your ${formatPlanTypes(changed)} ${
      changed.length === 1 ? 'plan will' : 'plans will'
    } change${nextYearText}`
    const sameText = `your ${formatPlanTypes(same)} ${
      same.length === 1 ? 'is' : 'are'
    } staying the same`
    return `${changedText}, but ${sameText}`
  }

  // Fallback cases for edge scenarios
  if (added.length > 0 && same.length > 0) {
    const addedText = `You have added ${formatPlanTypes(added)} ${
      added.length === 1 ? 'plan' : 'plans'
    }`
    const sameText = `your ${formatPlanTypes(same)} ${
      same.length === 1 ? 'is' : 'are'
    } staying the same`
    return `${addedText}, but ${sameText}`
  }

  if (added.length > 0) {
    return `You have added ${formatPlanTypes(added)} ${
      added.length === 1 ? 'plan' : 'plans'
    }`
  }

  if (changed.length > 0) {
    return `Your ${formatPlanTypes(changed)} ${
      changed.length === 1 ? 'plan will' : 'plans will'
    } change${nextYearText}`
  }

  if (removed.length > 0) {
    return `You have opted out from ${formatPlanTypes(removed)} ${
      removed.length === 1 ? 'plan' : 'plans'
    }`
  }

  // Default fallback
  return `You'll have the same plans${nextYearText}`
}

export const getPrevPlansWithMetadata = ({
  currentYearPlanNames,
  planCategories,
}: {
  currentYearPlanNames: PlanWithType[];
  planCategories: {
    added: string[];
    removed: string[];
    changed: string[];
    same: string[];
  };
}) => {
  return currentYearPlanNames.map((plan) => {
    const isRemovedOrChanged =
      planCategories.removed.includes(plan.type) ||
      planCategories.changed.includes(plan.type)
    return {
      value: plan.name,
      status: isRemovedOrChanged
        ? ('removed_or_changed' as const)
        : ('unchanged' as const),
    }
  })
}

export const getNextPlansWithMetadata = ({
  nextYearPlanNames,
  planCategories,
}: {
  nextYearPlanNames: PlanWithType[];
  planCategories: {
    added: string[];
    removed: string[];
    changed: string[];
    same: string[];
  };
}) => {
  return nextYearPlanNames.map((plan) => {
    const isAddedOrChanged =
      planCategories.added.includes(plan.type) ||
      planCategories.changed.includes(plan.type)
    return {
      value: plan.name,
      status: isAddedOrChanged
        ? ('added_or_changed' as const)
        : ('unchanged' as const),
    }
  })
}

export const SelectedPlansComparisonTable: React.FC<
  SelectedPlansComparisonTableProps
> = ({ planChangeSummary, onboardingData }) => {
  const isRenewal = isEnrollmentEventRenewal(onboardingData?.enrollment_event)
  const currentPlan = planChangeSummary.current_plan_year
  const nextPlan = planChangeSummary.next_plan_year

  const currentPlanYear = currentPlan.plan_year
  const nextPlanYear = nextPlan.plan_year

  /* Get current plans */
  const currentMedicalPlan = currentPlan.medical_plan
  const currentDentalPlan = currentPlan.dental_plan
  const currentVisionPlan = currentPlan.vision_plan

  /* Get next plans */
  const nextYearEnrollments = onboardingData?.enrollments
  const nextMedicalPlan = nextYearEnrollments?.find(
    (e: OnboardingEnrollment) => e.covers_medical === true
  )
  const nextDentalPlan = nextYearEnrollments?.find(
    (e: OnboardingEnrollment) => e.covers_dental === true
  )
  const nextVisionPlan = nextYearEnrollments?.find(
    (e: OnboardingEnrollment) => e.covers_vision === true
  )

  /* Get covered plans */
  const coveredPlans = ['medical']
  if (!onboardingData?.restrictions?.dental_not_available) {
    coveredPlans.push('dental')
  }
  if (!onboardingData?.restrictions?.vision_not_available) {
    coveredPlans.push('vision')
  }

  /* Generate plan names using testable functions */
  const currentYearPlanNames = getCurrentYearPlanNames({
    currentMedicalPlan,
    currentDentalPlan,
    currentVisionPlan,
  })

  const nextYearPlanNames = getNextYearPlanNames({
    nextMedicalPlan,
    nextDentalPlan,
    nextVisionPlan,
  })

  const planCategories = categorizePlanChanges({
    currentMedicalPlan,
    currentDentalPlan,
    currentVisionPlan,
    nextMedicalPlan,
    nextDentalPlan,
    nextVisionPlan,
  })

  const subtitle = generatePlanSubtitle(planCategories, coveredPlans, isRenewal)

  // Add status metadata to plan names for styling
  const prevValuesWithMetadata = getPrevPlansWithMetadata({
    currentYearPlanNames,
    planCategories,
  })

  const currValuesWithMetadata = getNextPlansWithMetadata({
    nextYearPlanNames,
    planCategories,
  })

  return (
    <div className='flex flex-col'>
      <h2 className='heading-sm text-gray-800 mb-2'>Your plans</h2>
      <div className='body text-gray-500 mb-4'>{subtitle}</div>
      <div className='flex flex-col gap-y-3'>
        <PartialCoverageWarningBanner
          planChangeSummary={planChangeSummary}
          coverageType='dental'
        />
        <PartialCoverageWarningBanner
          planChangeSummary={planChangeSummary}
          coverageType='vision'
        />
        <RenewalsConfirmComparisonTable
          prevPlanYear={currentPlanYear}
          currPlanYear={nextPlanYear}
          prevValues={prevValuesWithMetadata}
          currValues={currValuesWithMetadata}
        />
      </div>

    </div>
  )
}
